import { useCallback, useRef } from "react";
import { v4 } from "uuid";

import ExportablesTemplateHeaderSection from "@/components/AIComponents/ExportablesModal/templates/components/ExportablesTemplateHeaderSection";
import { TemplateTextArea } from "@/components/AIComponents/ExportablesModal/templates/components/TemplateTextArea";
import { TemplateProps } from "@/components/AIComponents/ExportablesModal/templates/registry";
import { type ClaimsEvidenceReasoningFormData } from "@/types/models/Exportables.interface";
import {
  containerStyles,
  contentStyles,
  fieldContentWrapperStyles,
  fieldLabelStyles,
  fieldStyles,
  textareaStyles,
} from "./ClaimsEvidenceReasoning.styles";
import { createFieldsData, FieldProps, isContextCluesFormData } from "./utils";

const CUSTOM_INPUT_MAX_LENGTH = 220;

const ClaimsEvidenceReasoning = ({
  formData,
  onChange,
}: TemplateProps<ClaimsEvidenceReasoningFormData>) => {
  const uniqueId = useRef(v4()).current;
  const cerData = isContextCluesFormData(formData)
    ? formData
    : ({} as ClaimsEvidenceReasoningFormData);

  const fieldsData = createFieldsData();

  const renderField = useCallback(
    (fields: FieldProps[]) => {
      return (
        <div css={fieldStyles}>
          {fields.map(({ label, field }) => {
            const textareaId = `cer-${field}-input-${uniqueId}`;
            const trimmedValue = `${cerData[field] || ""}`.substring(
              0,
              CUSTOM_INPUT_MAX_LENGTH,
            );

            return (
              <div key={textareaId} css={fieldContentWrapperStyles}>
                <p css={fieldLabelStyles}>{label}</p>
                <TemplateTextArea
                  value={trimmedValue}
                  maxLength={CUSTOM_INPUT_MAX_LENGTH}
                >
                  <TemplateTextArea.TextArea
                    id={textareaId}
                    data-qa-selector={textareaId}
                    onChange={(e) =>
                      onChange?.({ ...cerData, [field]: e.target.value })
                    }
                    css={textareaStyles}
                  />
                  <TemplateTextArea.CharacterCount />
                </TemplateTextArea>
              </div>
            );
          })}
        </div>
      );
    },
    [cerData, uniqueId, onChange],
  );

  return (
    <div css={containerStyles}>
      <ExportablesTemplateHeaderSection
        templateTitle="Claim-Evidence-Reasoning"
        articleTitle={cerData.article_title}
      />

      <div css={contentStyles}>{fieldsData.map(renderField)}</div>
    </div>
  );
};

export default ClaimsEvidenceReasoning;
